import { BTN, type EntityId, type MatchState, type PlayerInput, type TeamIdx } from '@fafi/shared';

/**
 * D143 (playtest 5, reports 6/7/10) — who the human is, and which bots actually play.
 *
 * Two hard-coded lines in `main.ts` outlived the feature that needed them. The D45 local kickabout was
 * built for a solo player on team 0, so it seated the human on entity 1 and drove bot inputs only for
 * `p.team === 0 || p.isGk` — every opposing outfielder was a passive dummy on ZERO input. When D128
 * added drills and D138/D139 added lessons, both rode the same loop, and their `bots` / `playerTeam`
 * fields were declared, populated, documented — and never read by anything.
 *
 * The visible consequences the playtester found:
 *   · `beat-your-man` (1v1) has an opponent who never moves — measured 0.00 m in 10 s;
 *   · the free-kick "wall" never forms, because `wallPlan` runs for team 1 and its output is discarded
 *     — the defenders were not standing in a line by choice, they were standing still;
 *   · `keeper-week` ("you are the keeper") seats you on an outfielder while a bot keeps goal.
 */

/** The entity a scenario seats the human on. Slot 0 is the GK, so ids are `team * teamSize + slot`. */
export function seatFor(teamSize: number, team: TeamIdx, role: 'gk' | 'outfield' | undefined): EntityId {
  return (team * teamSize + (role === 'gk' ? 0 : 1)) as EntityId;
}

/**
 * Should this bot's input be applied?
 *
 * `'both'` is a full live rehearsal: every bot on both sides plays. `'defense'` keeps the D45 shape —
 * your own side plus both keepers — which is what an attacking drill wants, because a live opposition
 * would break up the rehearsal you are there to repeat.
 */
export function botPlays(bots: 'defense' | 'both', playerTeam: TeamIdx, botTeam: TeamIdx, isGk: boolean): boolean {
  return bots === 'both' || botTeam === playerTeam || isGk;
}

/** Apply a bot input map to the input array under the scenario's `bots` rule. */
export function applyBotInputs(
  state: MatchState,
  inputs: PlayerInput[],
  bots: ReadonlyMap<EntityId, PlayerInput>,
  rule: 'defense' | 'both',
  playerTeam: TeamIdx,
): void {
  for (const [id, inp] of bots) {
    const p = state.players[id];
    if (p && botPlays(rule, playerTeam, p.team, p.isGk)) inputs[id] = inp;
  }
}

/**
 * D152 (playtest 6, reports 4 + 7) — a lesson is a REHEARSAL, not a match.
 *
 * D143 was right to make every bot live, and it went one step too far: they play to win. The tester
 * found both halves of that — *"bots try to score goals in training. They shouldn't"*, and in the 1v1
 * *"opponent does not wait for me to take control. Then he attempts to score."*
 *
 * Two restraints, both client-side (the AI stays pure and match-shaped; a "training mode" has no
 * business inside `shared/src/ai`):
 *
 *  · **bots do not shoot.** You are the only one who scores in your own lesson. `keeper-week` is the
 *    deliberate exception — a penalty at your goal is the entire lesson — so it opts back in.
 *  · **the opposition holds** until you have touched the ball, capped at `HOLD_OPPONENTS_TICKS` so a
 *    DEFENDING rep (where the opponent starts with it) still begins on its own.
 */
export const HOLD_OPPONENTS_TICKS = 90; // 3 s — long enough to get set, short enough not to stall a defending rep

export interface Restraint {
  /** The entity the human drives — never restrained. */
  readonly seat: number;
  readonly playerTeam: TeamIdx;
  /** Opt back in to bots shooting (keeper-week). */
  readonly botsShoot: boolean;
  /** Hold the opposition still this tick (the rep has not started yet). */
  readonly holdOpponents: boolean;
}

const STILL: PlayerInput = { moveX: 0, moveY: 0, aimYaw: 0, aimElev: 0, buttons: 0, emote: 0 };

/** Apply the training restraints in place. A no-op for the human's own input. */
export function restrainBots(state: MatchState, inputs: PlayerInput[], r: Restraint): void {
  for (const p of state.players) {
    if (p.id === r.seat) continue;
    const inp = inputs[p.id];
    if (!inp) continue;
    if (r.holdOpponents && p.team !== r.playerTeam) {
      inputs[p.id] = STILL; // let him get set before anyone comes for him
      continue;
    }
    if (!r.botsShoot && (inp.buttons & BTN.SHOOT) !== 0) inputs[p.id] = { ...inp, buttons: inp.buttons & ~BTN.SHOOT };
  }
}
